import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { FileText, FileCsv, Download, Spinner, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_OPTIONS = [
    { value: 'all', label: 'Alle Typen' },
    { value: 'apartment', label: 'Wohnung' },
    { value: 'house', label: 'Haus' },
    { value: 'land', label: 'Grundstück' },
    { value: 'office', label: 'Büro' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'warehouse', label: 'Lager/Halle' },
    { value: 'other', label: 'Sonstige' },
];

const MARKETING_TYPE_OPTIONS = [
    { value: 'all', label: 'Alle' },
    { value: 'sale', label: 'Kauf' },
    { value: 'rent', label: 'Miete' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'Alle Status' },
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'sold', label: 'Verkauft' },
    { value: 'rented', label: 'Vermietet' },
];

export default function ExportPage() {
    const [brokers, setBrokers] = useState([]);
    const [filters, setFilters] = useState({
        property_type: 'all',
        marketing_type: 'all',
        status: 'all',
        broker_id: 'all'
    });
    const [exportingCsv, setExportingCsv] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [propertyCount, setPropertyCount] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [brokersRes, propertiesRes] = await Promise.all([
                    axios.get(`${API}/api/brokers`, { withCredentials: true }),
                    axios.get(`${API}/api/properties?limit=1`, { withCredentials: true })
                ]);
                setBrokers(brokersRes.data);
                setPropertyCount(propertiesRes.data.total);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    // Update property count when filters change
    useEffect(() => {
        const fetchCount = async () => {
            try {
                const params = new URLSearchParams();
                if (filters.property_type !== 'all') params.append('property_type', filters.property_type);
                if (filters.marketing_type !== 'all') params.append('marketing_type', filters.marketing_type);
                if (filters.status !== 'all') params.append('status', filters.status);
                if (filters.broker_id !== 'all') params.append('broker_id', filters.broker_id);
                params.append('limit', '1');
                
                const { data } = await axios.get(`${API}/api/properties?${params.toString()}`, { withCredentials: true });
                setPropertyCount(data.total);
            } catch (error) {
                console.error('Error fetching count:', error);
            }
        };
        fetchCount();
    }, [filters]);

    const buildExportUrl = (format) => {
        const params = new URLSearchParams();
        if (filters.property_type !== 'all') params.append('property_type', filters.property_type);
        if (filters.marketing_type !== 'all') params.append('marketing_type', filters.marketing_type);
        if (filters.status !== 'all') params.append('status', filters.status);
        if (filters.broker_id !== 'all') params.append('broker_id', filters.broker_id);
        
        return `${API}/api/export/${format}?${params.toString()}`;
    };

    const handleExport = async (format) => {
        if (format === 'csv') {
            setExportingCsv(true);
        } else {
            setExportingPdf(true);
        }

        try {
            const response = await axios.get(buildExportUrl(format), {
                withCredentials: true,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from header or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `immobilien_export.${format}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename=([^;]+)/);
                if (match) {
                    filename = match[1].replace(/"/g, '');
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`${format.toUpperCase()} Export erfolgreich`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error(`Fehler beim ${format.toUpperCase()} Export`);
        }

        if (format === 'csv') {
            setExportingCsv(false);
        } else {
            setExportingPdf(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="export-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Export
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Filter Card */}
                <Card className="border" data-testid="export-filters-card">
                    <CardHeader>
                        <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            Filter
                        </CardTitle>
                        <CardDescription>
                            Wählen Sie die zu exportierenden Immobilien aus
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Immobilientyp</Label>
                            <Select 
                                value={filters.property_type} 
                                onValueChange={(v) => setFilters({ ...filters, property_type: v })}
                            >
                                <SelectTrigger className="rounded-sm" data-testid="export-type-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROPERTY_TYPE_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Vermarktungsart</Label>
                            <Select 
                                value={filters.marketing_type} 
                                onValueChange={(v) => setFilters({ ...filters, marketing_type: v })}
                            >
                                <SelectTrigger className="rounded-sm" data-testid="export-marketing-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MARKETING_TYPE_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Status</Label>
                            <Select 
                                value={filters.status} 
                                onValueChange={(v) => setFilters({ ...filters, status: v })}
                            >
                                <SelectTrigger className="rounded-sm" data-testid="export-status-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUS_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Makler</Label>
                            <Select 
                                value={filters.broker_id} 
                                onValueChange={(v) => setFilters({ ...filters, broker_id: v })}
                            >
                                <SelectTrigger className="rounded-sm" data-testid="export-broker-filter">
                                    <SelectValue placeholder="Alle Makler" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Makler</SelectItem>
                                    {brokers.map((broker) => (
                                        <SelectItem key={broker.id} value={broker.id}>
                                            {broker.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Gefundene Immobilien:</span>
                                <span className="text-lg font-bold" data-testid="property-count">{propertyCount}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Export Options Card */}
                <Card className="border" data-testid="export-options-card">
                    <CardHeader>
                        <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            Export-Format
                        </CardTitle>
                        <CardDescription>
                            Wählen Sie das gewünschte Exportformat
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* CSV Export */}
                        <div className="p-4 border rounded-sm hover:border-primary/50 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-green-100 rounded-sm">
                                    <FileCsv size={24} className="text-green-600" weight="fill" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold">CSV Export</h3>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Tabellendaten für Excel, Google Sheets oder andere Tabellenkalkulationen. 
                                        Enthält alle Immobiliendaten in strukturierter Form.
                                    </p>
                                    <Button 
                                        onClick={() => handleExport('csv')}
                                        disabled={exportingCsv || propertyCount === 0}
                                        className="rounded-sm"
                                        data-testid="export-csv-btn"
                                    >
                                        {exportingCsv ? (
                                            <>
                                                <Spinner className="animate-spin mr-2" size={16} />
                                                Exportieren...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={16} className="mr-2" />
                                                Als CSV herunterladen
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* PDF Export */}
                        <div className="p-4 border rounded-sm hover:border-primary/50 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-red-100 rounded-sm">
                                    <FileText size={24} className="text-red-600" weight="fill" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold">PDF Export</h3>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Formatiertes Dokument mit allen Immobiliendetails. 
                                        Ideal zum Drucken oder Teilen per E-Mail.
                                    </p>
                                    <Button 
                                        onClick={() => handleExport('pdf')}
                                        disabled={exportingPdf || propertyCount === 0}
                                        variant="outline"
                                        className="rounded-sm"
                                        data-testid="export-pdf-btn"
                                    >
                                        {exportingPdf ? (
                                            <>
                                                <Spinner className="animate-spin mr-2" size={16} />
                                                Exportieren...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={16} className="mr-2" />
                                                Als PDF herunterladen
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info Card */}
            <Card className="border" data-testid="export-info-card">
                <CardHeader>
                    <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Exportierte Daten
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <FileCsv size={18} className="text-green-600" />
                                CSV enthält:
                            </h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Alle Objektdaten (ID, Titel, Typ, etc.)</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Preise und Flächen</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Vollständige Adressen</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Beschreibungen (gekürzt)</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Ausstattungsmerkmale</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <FileText size={18} className="text-red-600" />
                                PDF enthält:
                            </h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Formatierte Übersicht</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Objektdetails pro Immobilie</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Beschreibungstexte</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Ausstattungslisten</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-green-600" /> Druckoptimiertes Layout</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
